import { supabase } from '../lib/supabase';
import { captureError } from '../lib/sentry';

/**
 * Audit logging service.
 *
 * Juuni logs sensitive user actions to admin_audit_log for:
 *   - Customer "who changed this?" questions
 *   - SOC 2 Type I / Type II audit trail requirement
 *   - Security forensics (role changes, impersonation starts, destructive ops)
 *
 * Two entry points:
 *   1. logAudit(action, meta)  — preferred. Uses SECURITY DEFINER RPC
 *      which captures actor_id/org_id/role server-side from JWT claims, so
 *      callers cannot spoof identity. Also captures actor_role snapshot
 *      (crucial when users get demoted after an action).
 *
 *   2. logAdminAction({ orgId, userId, ... })  — original API. Preserved
 *      for backward compat with existing callers (JobPostingForm,
 *      useAdminActions, AnnotatorProfile). Internally now ALSO goes through
 *      the RPC so old and new callers produce identical rows. Accepts
 *      explicit actor identity — the RPC ignores those and uses JWT anyway.
 *
 * Both entry points are fire-and-forget. If audit logging fails, we
 * capture to Sentry but NEVER throw — the underlying user action
 * (publishing a report, approving time, etc.) must complete.
 *
 * Append-only guarantee: the admin_audit_log table had UPDATE / DELETE /
 * TRUNCATE privileges REVOKEd from anon + authenticated + service_role in
 * migration 056. Only postgres superuser can modify rows (emergency only).
 */

/**
 * Canonical action types. Action field on the table is free-text (no CHECK
 * constraint — deliberately, so we can add new actions without migrations),
 * but new call sites MUST use one of these constants to avoid typos
 * silently scattering across the audit trail.
 *
 * To add a new action: add it here, reference the constant at the call
 * site, and update the AUDIT_ACTION_LABELS map below for viewer display.
 */
export const AUDIT_ACTIONS = Object.freeze({
  // Assignment lifecycle
  DELETE_ASSIGNMENT:   'delete_assignment',
  CHANGE_RATE:         'change_rate',

  // Report lifecycle
  PUBLISH_REPORT:      'publish_report',
  UNPUBLISH_REPORT:    'unpublish_report',

  // Time review
  APPROVE_TIME_ENTRY:  'approve_time_entry',
  REJECT_TIME_ENTRY:   'reject_time_entry',

  // Project lifecycle
  ARCHIVE_PROJECT:     'archive_project',

  // User / access changes
  INVITE_USER:         'invite_user',
  IMPERSONATE_START:   'impersonate_start',
  // Note: CHANGE_ROLE removed 2026-04-18 — no role-change UI exists; roles
  // are set at signup via authBootstrap and mirrored to auth.users.app_metadata
  // by the sync_role_to_auth Postgres trigger. Re-add if a role-change UI lands.

  // GDPR — data lifecycle
  EXPORT_ORG_DATA:               'export_org_data',
  EXPORT_MY_DATA:                'export_my_data',
  REQUEST_ACCOUNT_DELETION:      'request_account_deletion',
  CANCEL_DELETION_REQUEST:       'cancel_deletion_request',
  // Phase 11 slot 4 (2026-04-24) — Articles 16/17 (soft-delete) / 18
  GDPR_DELETION_FULFILLED:       'gdpr_deletion_fulfilled',
  GDPR_DELETION_REJECTED:        'gdpr_deletion_rejected',
  GDPR_RECTIFICATION_REQUESTED:  'gdpr_rectification_requested',
  GDPR_RECTIFICATION_FULFILLED:  'gdpr_rectification_fulfilled',
  GDPR_RECTIFICATION_REJECTED:   'gdpr_rectification_rejected',
  GDPR_RESTRICTION_REQUESTED:    'gdpr_restriction_requested',
  GDPR_RESTRICTION_LIFTED:       'gdpr_restriction_lifted',

  // Dataset lifecycle (Phase 5.75)
  TRANSITION_DATASET_STATE: 'transition_dataset_state',
  CREATE_DATASET:           'create_dataset',
  UPDATE_DATASET:           'update_dataset',
  DELETE_DATASET:           'delete_dataset',
  UPDATE_DATASET_PROVENANCE: 'update_dataset_provenance',

  // Saved views / slices (Phase 5.75 Stage B — wired via Stage C audit gap fix)
  CREATE_SAVED_VIEW:    'create_saved_view',
  UPDATE_SAVED_VIEW:    'update_saved_view',
  DELETE_SAVED_VIEW:    'delete_saved_view',
  DUPLICATE_SAVED_VIEW: 'duplicate_saved_view',

  // CSV ingest (Phase 6) — UI-triggered events. Row-level audit lives in
  // annotationRecordService.recordAnnotationsBulk via existing
  // 'record_annotations_bulk' event. These events capture the INGEST
  // lifecycle (the user-visible flow), NOT per-row writes.
  //
  // CSV_INGEST_STARTED   — user initiated upload
  // CSV_INGEST_PREVIEWED — parse completed, Preview state rendered
  // CSV_INGEST_COMMITTED — user clicked Commit; ingest_csv_commit RPC succeeded
  // CSV_INGEST_CANCELLED — user clicked Cancel from Preview (no data written)
  // CSV_INGEST_FAILED    — actor-visible failure (RPC or pre-RPC error).
  //                        DB-side failures also land in ingest_log.success=false,
  //                        so this event is for UI-path failures where the
  //                        caller wants a trail beyond the DB log.
  CSV_INGEST_STARTED:   'csv_ingest_started',
  CSV_INGEST_PREVIEWED: 'csv_ingest_previewed',
  CSV_INGEST_COMMITTED: 'csv_ingest_committed',
  CSV_INGEST_CANCELLED: 'csv_ingest_cancelled',
  CSV_INGEST_FAILED:    'csv_ingest_failed',

  // LLM digest (Phase 8 MVP) — weekly SPL digest narrated by Claude.
  // Lifecycle: generate → (dry-run: approve → send) OR (auto: send)
  // Skipped variants: cost-cap hit, no email on file, idempotent dupe.
  //
  // DIGEST_GENERATED           — edge function generated a draft row
  // DIGEST_APPROVED            — admin approved a draft (dry-run mode)
  // DIGEST_SENT                — dispatched to Resend API successfully
  // DIGEST_FAILED              — generation or send errored; row persisted
  //                              with status=failed + error details in meta
  // DIGEST_SKIPPED_COST_CAP    — org exceeded weekly LLM spend cap
  // DIGEST_SKIPPED_NO_EMAIL    — target SPL has null/invalid email
  // DIGEST_SKIPPED_IDEMPOTENT  — duplicate fire for existing (org,spl,week)
  DIGEST_GENERATED:          'digest_generated',
  DIGEST_APPROVED:           'digest_approved',
  DIGEST_SENT:               'digest_sent',
  DIGEST_FAILED:             'digest_failed',
  DIGEST_SKIPPED_COST_CAP:   'digest_skipped_cost_cap',
  DIGEST_SKIPPED_NO_EMAIL:   'digest_skipped_no_email',
  DIGEST_SKIPPED_IDEMPOTENT: 'digest_skipped_idempotent',

  // ─── Phase 11 slot 5 (2026-04-24): comprehensive audit-trail sweep ───
  // The Phase 11 slot 5 service-level grep (audit 5.1 HIGH) surfaced ~30
  // admin mutations across 15 services that produced no audit row. New
  // constants below close those gaps. Group order mirrors the report.

  // Batch lifecycle (batchService.js)
  // TRANSITION_BATCH_STATE — the 4-state lifecycle pivot, including
  //   bounce-backs that increment revision_count. updateBatchStatus is the
  //   single funnel — approveAndDeliver / rejectWithNote / markAccepted /
  //   markRejectedByClient all route through it.
  // ASSIGN_BATCH / REASSIGN_BATCH / ASSIGN_REVISION — work-ownership flips
  //   on individual_id. Reassignment resets items_completed/accepted/
  //   rejected and is destructive to annotator progress.
  // CLEAR_BATCHES_FOR_JOB — destructive bulk delete of all batches +
  //   quality_log for a job. High-blast-radius admin op.
  // CREATE_REWORK_BATCH — creates a new batch AND resets parent
  //   items_rejected (two writes).
  TRANSITION_BATCH_STATE:    'transition_batch_state',
  ASSIGN_BATCH:              'assign_batch',
  REASSIGN_BATCH:            'reassign_batch',
  ASSIGN_REVISION:           'assign_revision',
  CLEAR_BATCHES_FOR_JOB:     'clear_batches_for_job',
  CREATE_REWORK_BATCH:       'create_rework_batch',

  // QA routing (qaRoutingService.js)
  // QA_ROUTE_BATCH — every routeBatchToQA call writes to annotation_batch
  //   across 5 different code paths (no-config, skip-rate, peer,
  //   client_review, gold-standard pass/fail).
  // QA_COMPUTE_OVERLAP — sets qa_score + terminal state for ALL batches
  //   in an overlap group.
  QA_ROUTE_BATCH:            'qa_route_batch',
  QA_COMPUTE_OVERLAP:        'qa_compute_overlap',

  // Client report (clientReportService.js)
  // GENERATE_REPORT — drafts a report (cost + PII implication).
  // ARCHIVE_REPORT / DELETE_REPORT — state changes on client-facing artifact.
  // UPDATE_REPORT_NOTES — allowed even on published reports → trail required.
  // RESOLVE_ANNOTATION_ERRORS / UPDATE_ERROR_CATEGORIES — admin override
  //   on shared error log.
  GENERATE_REPORT:           'generate_report',
  ARCHIVE_REPORT:            'archive_report',
  DELETE_REPORT:             'delete_report',
  UPDATE_REPORT_NOTES:       'update_report_notes',
  RESOLVE_ANNOTATION_ERRORS: 'resolve_annotation_errors',
  UPDATE_ERROR_CATEGORIES:   'update_error_categories',

  // Time entries (timeEntryService.js)
  // BULK_APPROVE_TIME_ENTRIES — admin end-of-week approval path; the
  //   per-entry APPROVE_TIME_ENTRY path doesn't fire from the bulk caller.
  BULK_APPROVE_TIME_ENTRIES: 'bulk_approve_time_entries',

  // Guideline lifecycle (guidelineService.js)
  // PUBLISH_GUIDELINE is the load-bearing event — guidelines define the
  // contract between annotator and project at publish time.
  CREATE_GUIDELINE:          'create_guideline',
  UPDATE_GUIDELINE_DRAFT:    'update_guideline_draft',
  PUBLISH_GUIDELINE:         'publish_guideline',
  DELETE_GUIDELINE_DRAFT:    'delete_guideline_draft',

  // Deliverables (deliverableService.js)
  // Deliverables are client-facing handoff artifacts; state transitions
  // map to money changing hands.
  CREATE_DELIVERABLE:        'create_deliverable',
  UPDATE_DELIVERABLE_STATUS: 'update_deliverable_status',
  DELETE_DELIVERABLE:        'delete_deliverable',

  // Calibration (calibrationService.js)
  // QA training records — lower priority but admin-only writes on shared
  // calibration_session table.
  CREATE_CALIBRATION_SESSION: 'create_calibration_session',
  UPDATE_CALIBRATION_SESSION: 'update_calibration_session',
  DELETE_CALIBRATION_SESSION: 'delete_calibration_session',

  // Client requests (clientService.js)
  // SUBMIT_CLIENT_REQUEST — generic event for any client_request row creation
  //   (additional_items / timeline_change / staffing_change / scope_increase /
  //    status_update / re_review / dispute). Wired in slot 5 sweep for
  //    audit-trail completeness; surfaced in Phase 13 minimal alongside
  //    RAISE_DISPUTE.
  // RAISE_DISPUTE — Phase 13 minimal. Specifically for `request_type='dispute'`
  //    submissions: client formally flagging an Accepted batch. Distinct
  //    from SUBMIT_CLIENT_REQUEST (the parent event) so disputes stand out
  //    in the audit log + can drive PM-notification routing.
  // RESOLVE_DISPUTE — Phase 13 minimal companion to RAISE_DISPUTE. Fires
  //    when admin closes out a dispute via the existing
  //    respondToClientRequest path with status='approved' or 'rejected'.
  //    The dispute-specific event lets us track resolution-time as a
  //    customer-trust metric; the generic RESPOND_TO_CLIENT_REQUEST still
  //    fires for the underlying state change.
  // RESPOND_TO_CLIENT_REQUEST — admin response to a client_request row.
  SUBMIT_CLIENT_REQUEST:     'submit_client_request',
  RAISE_DISPUTE:             'raise_dispute',
  RESOLVE_DISPUTE:           'resolve_dispute',
  RESPOND_TO_CLIENT_REQUEST: 'respond_to_client_request',

  // Phase 5.75-vocab Commit A (2026-04-26) — Client Portal user linkage.
  // Replaces the email-fallback in `resolve_current_client_access` with
  // explicit junction-table membership via `client_reviewer`. See
  // docs/backbone/phase-5-75-vocab-followup-context-brief.md.
  // - LINK_CLIENT_USER fires when admin adds a portal user WITHOUT sending
  //   an invite (e.g., "this individual already has an account; just grant
  //   them access"). The membership is active immediately.
  // - INVITE_CLIENT_USER fires when admin adds a portal user AND sends a
  //   magic-link invite. The membership is active immediately too — but
  //   the access_token+invite_sent_at provide a one-click signup path for
  //   the invitee.
  // - REVOKE_CLIENT_USER fires when admin removes a portal user's access
  //   (soft-revoke: active=false, removed_at, removed_by).
  // - ACCEPT_CLIENT_INVITE fires when an invited user clicks the magic
  //   link, sets a password (or signs in), and the redemption RPC links
  //   their auth.user.id to the membership row + clears the access_token.
  LINK_CLIENT_USER:          'link_client_user',
  INVITE_CLIENT_USER:        'invite_client_user',
  REVOKE_CLIENT_USER:        'revoke_client_user',
  ACCEPT_CLIENT_INVITE:      'accept_client_invite',

  // Stripe (stripeService.js)
  // PROCESS_PAYOUTS — creates payout rows + flips transactions to
  //   'Processing'; moves real or stub money. Highest-risk admin op.
  // STRIPE_WEBHOOK_APPLIED — transfer.paid / transfer.failed flip payout +
  //   transaction statuses. Money-state machine; audit even when machine-
  //   driven.
  PROCESS_PAYOUTS:           'process_payouts',
  STRIPE_WEBHOOK_APPLIED:    'stripe_webhook_applied',

  // Earnings (earningsService.js)
  MARK_TRANSACTIONS_PAID:    'mark_transactions_paid',

  // Stripe billing — Juuni invoices to clients (stripeBillingService.js)
  ISSUE_INVOICE:             'issue_invoice',
  VOID_INVOICE:              'void_invoice',
  BILLING_WEBHOOK_APPLIED:   'billing_webhook_applied',
  STRIPE_CUSTOMER_LINKED:    'stripe_customer_linked',

  // Onboarding (onboardingService.js)
  // BYPASS_ONBOARDING — admin fast-track of an annotator's onboarding.
  // PROMOTE_ASSIGNMENT — flips assignment.status to Active.
  BYPASS_ONBOARDING:         'bypass_onboarding',
  PROMOTE_ASSIGNMENT:        'promote_assignment',

  // Offboarding (offboardingService.js)
  // INITIALIZE_OFFBOARDING — pre-termination state change.
  // COMPLETE_OFFBOARDING — terminal user state (status: Inactive).
  INITIALIZE_OFFBOARDING:    'initialize_offboarding',
  UPDATE_OFFBOARDING_STEP:   'update_offboarding_step',
  COMPLETE_OFFBOARDING:      'complete_offboarding',
  CANCEL_OFFBOARDING:        'cancel_offboarding',

  // Consent / activation (consentService.js)
  // ACTIVATE_ACCOUNT — token-exchange that flips consent_status/status to
  //   Active and captures consent_given_at (GDPR-material).
  ACTIVATE_ACCOUNT:          'activate_account',

  // Tags (tagService.js) — §8.5 row 7 backfill.
  // Single canonical action; addTagToDataset / removeTagFromDataset funnel
  // through setDatasetTags, so a single audit at the funnel suffices.
  UPDATE_DATASET_TAGS:       'update_dataset_tags',
});

/**
 * Human-readable labels for the audit-viewer page. Keep in sync with
 * AUDIT_ACTIONS above. Verbs are past tense — reads naturally in a log.
 */
export const AUDIT_ACTION_LABELS = Object.freeze({
  [AUDIT_ACTIONS.DELETE_ASSIGNMENT]:  'deleted an assignment',
  [AUDIT_ACTIONS.CHANGE_RATE]:        'changed a pay rate',
  [AUDIT_ACTIONS.PUBLISH_REPORT]:     'published a client report',
  [AUDIT_ACTIONS.UNPUBLISH_REPORT]:   'unpublished a client report',
  [AUDIT_ACTIONS.APPROVE_TIME_ENTRY]: 'approved a time entry',
  [AUDIT_ACTIONS.REJECT_TIME_ENTRY]:  'rejected a time entry',
  [AUDIT_ACTIONS.ARCHIVE_PROJECT]:    'archived a project',
  [AUDIT_ACTIONS.INVITE_USER]:        'invited a user',
  [AUDIT_ACTIONS.IMPERSONATE_START]:  'started impersonating another user',

  // GDPR — data lifecycle
  [AUDIT_ACTIONS.EXPORT_ORG_DATA]:               'exported organization data (GDPR Art 20 portability)',
  [AUDIT_ACTIONS.EXPORT_MY_DATA]:                'exported own data (GDPR Art 15 right of access)',
  [AUDIT_ACTIONS.REQUEST_ACCOUNT_DELETION]:      'requested account/org deletion (GDPR Art 17 erasure)',
  [AUDIT_ACTIONS.CANCEL_DELETION_REQUEST]:       'cancelled a pending deletion request',
  [AUDIT_ACTIONS.GDPR_DELETION_FULFILLED]:       'fulfilled a deletion request via PII anonymization (GDPR Art 17)',
  [AUDIT_ACTIONS.GDPR_DELETION_REJECTED]:        'rejected a deletion request',
  [AUDIT_ACTIONS.GDPR_RECTIFICATION_REQUESTED]:  'requested rectification of a personal-data field (GDPR Art 16)',
  [AUDIT_ACTIONS.GDPR_RECTIFICATION_FULFILLED]:  'applied a user-requested rectification (GDPR Art 16)',
  [AUDIT_ACTIONS.GDPR_RECTIFICATION_REJECTED]:   'rejected a rectification request',
  [AUDIT_ACTIONS.GDPR_RESTRICTION_REQUESTED]:    'requested restriction of processing (GDPR Art 18)',
  [AUDIT_ACTIONS.GDPR_RESTRICTION_LIFTED]:       'lifted processing restriction',

  // Deliverable lifecycle (Phase 5.75; UI label "Deliverable" / DB stays `dataset`)
  [AUDIT_ACTIONS.TRANSITION_DATASET_STATE]:  'transitioned deliverable state',
  [AUDIT_ACTIONS.CREATE_DATASET]:            'created a deliverable',
  [AUDIT_ACTIONS.UPDATE_DATASET]:            'updated a deliverable',
  [AUDIT_ACTIONS.DELETE_DATASET]:            'deleted a deliverable',
  [AUDIT_ACTIONS.UPDATE_DATASET_PROVENANCE]: 'updated deliverable provenance',

  // Saved views / slices
  [AUDIT_ACTIONS.CREATE_SAVED_VIEW]:    'created a saved view',
  [AUDIT_ACTIONS.UPDATE_SAVED_VIEW]:    'updated a saved view',
  [AUDIT_ACTIONS.DELETE_SAVED_VIEW]:    'deleted a saved view',
  [AUDIT_ACTIONS.DUPLICATE_SAVED_VIEW]: 'duplicated a saved view',

  // CSV ingest (Phase 6)
  [AUDIT_ACTIONS.CSV_INGEST_STARTED]:   'started a CSV ingest upload',
  [AUDIT_ACTIONS.CSV_INGEST_PREVIEWED]: 'previewed a CSV ingest',
  [AUDIT_ACTIONS.CSV_INGEST_COMMITTED]: 'committed a CSV ingest',
  [AUDIT_ACTIONS.CSV_INGEST_CANCELLED]: 'cancelled a CSV ingest',
  [AUDIT_ACTIONS.CSV_INGEST_FAILED]:    'CSV ingest failed',

  // LLM digest (Phase 8 MVP)
  [AUDIT_ACTIONS.DIGEST_GENERATED]:          'generated a weekly SPL digest (draft)',
  [AUDIT_ACTIONS.DIGEST_APPROVED]:           'approved a weekly digest for send',
  [AUDIT_ACTIONS.DIGEST_SENT]:               'sent a weekly SPL digest',
  [AUDIT_ACTIONS.DIGEST_FAILED]:             'weekly digest generation or send failed',
  [AUDIT_ACTIONS.DIGEST_SKIPPED_COST_CAP]:   'weekly digest skipped (org weekly cost cap reached)',
  [AUDIT_ACTIONS.DIGEST_SKIPPED_NO_EMAIL]:   'weekly digest skipped (SPL has no email)',
  [AUDIT_ACTIONS.DIGEST_SKIPPED_IDEMPOTENT]: 'weekly digest skipped (already exists for this week)',

  // ─── Phase 11 slot 5 (2026-04-24) backfill labels ───
  // Batch lifecycle
  [AUDIT_ACTIONS.TRANSITION_BATCH_STATE]:    'transitioned a batch state',
  [AUDIT_ACTIONS.ASSIGN_BATCH]:              'assigned a batch to an annotator',
  [AUDIT_ACTIONS.REASSIGN_BATCH]:            'reassigned a batch to a different annotator',
  [AUDIT_ACTIONS.ASSIGN_REVISION]:           'assigned a revision batch',
  [AUDIT_ACTIONS.CLEAR_BATCHES_FOR_JOB]:     'cleared all batches for a project',
  [AUDIT_ACTIONS.CREATE_REWORK_BATCH]:       'created a rework batch',
  // Quality routing
  [AUDIT_ACTIONS.QA_ROUTE_BATCH]:            'routed a batch through Quality review',
  [AUDIT_ACTIONS.QA_COMPUTE_OVERLAP]:        'computed overlap-agreement scores for an overlap group',
  // Client report
  [AUDIT_ACTIONS.GENERATE_REPORT]:           'generated a client report draft',
  [AUDIT_ACTIONS.ARCHIVE_REPORT]:            'archived a client report',
  [AUDIT_ACTIONS.DELETE_REPORT]:             'deleted a client report',
  [AUDIT_ACTIONS.UPDATE_REPORT_NOTES]:       'updated client-report notes',
  [AUDIT_ACTIONS.RESOLVE_ANNOTATION_ERRORS]: 'resolved annotation errors',
  [AUDIT_ACTIONS.UPDATE_ERROR_CATEGORIES]:   'updated annotation error categories',
  // Time entries
  [AUDIT_ACTIONS.BULK_APPROVE_TIME_ENTRIES]: 'bulk-approved time entries',
  // Guidelines
  [AUDIT_ACTIONS.CREATE_GUIDELINE]:          'created a guideline',
  [AUDIT_ACTIONS.UPDATE_GUIDELINE_DRAFT]:    'updated a guideline draft',
  [AUDIT_ACTIONS.PUBLISH_GUIDELINE]:         'published a guideline',
  [AUDIT_ACTIONS.DELETE_GUIDELINE_DRAFT]:    'deleted a guideline draft',
  // Deliverables
  [AUDIT_ACTIONS.CREATE_DELIVERABLE]:        'created a deliverable',
  [AUDIT_ACTIONS.UPDATE_DELIVERABLE_STATUS]: 'updated a deliverable status',
  [AUDIT_ACTIONS.DELETE_DELIVERABLE]:        'deleted a deliverable',
  // Calibration
  [AUDIT_ACTIONS.CREATE_CALIBRATION_SESSION]: 'created a calibration session',
  [AUDIT_ACTIONS.UPDATE_CALIBRATION_SESSION]: 'updated a calibration session',
  [AUDIT_ACTIONS.DELETE_CALIBRATION_SESSION]: 'deleted a calibration session',
  // Client requests
  [AUDIT_ACTIONS.SUBMIT_CLIENT_REQUEST]:     'submitted a client request',
  [AUDIT_ACTIONS.RAISE_DISPUTE]:             'raised a batch dispute',
  [AUDIT_ACTIONS.RESOLVE_DISPUTE]:           'resolved a batch dispute',
  [AUDIT_ACTIONS.RESPOND_TO_CLIENT_REQUEST]: 'responded to a client request',
  // Client portal user linkage (Phase 5.75-vocab Commit A)
  [AUDIT_ACTIONS.LINK_CLIENT_USER]:          'linked a portal user to a client',
  [AUDIT_ACTIONS.INVITE_CLIENT_USER]:        'invited a portal user to a client',
  [AUDIT_ACTIONS.REVOKE_CLIENT_USER]:        "revoked a portal user's client access",
  [AUDIT_ACTIONS.ACCEPT_CLIENT_INVITE]:      'accepted a client portal invitation',
  // Stripe — payouts to annotators
  [AUDIT_ACTIONS.PROCESS_PAYOUTS]:           'processed payouts to annotators',
  [AUDIT_ACTIONS.STRIPE_WEBHOOK_APPLIED]:    'applied a Stripe transfer webhook',
  // Earnings
  [AUDIT_ACTIONS.MARK_TRANSACTIONS_PAID]:    'marked transactions as paid',
  // Stripe billing — invoices to clients
  [AUDIT_ACTIONS.ISSUE_INVOICE]:             'issued a Juuni invoice',
  [AUDIT_ACTIONS.VOID_INVOICE]:              'voided a Juuni invoice',
  [AUDIT_ACTIONS.BILLING_WEBHOOK_APPLIED]:   'applied a Stripe billing webhook',
  [AUDIT_ACTIONS.STRIPE_CUSTOMER_LINKED]:    'linked a Stripe customer',
  // Onboarding
  [AUDIT_ACTIONS.BYPASS_ONBOARDING]:         'bypassed onboarding for an annotator',
  [AUDIT_ACTIONS.PROMOTE_ASSIGNMENT]:        'promoted an assignment to Active',
  // Offboarding
  [AUDIT_ACTIONS.INITIALIZE_OFFBOARDING]:    'initialized offboarding',
  [AUDIT_ACTIONS.UPDATE_OFFBOARDING_STEP]:   'updated an offboarding step',
  [AUDIT_ACTIONS.COMPLETE_OFFBOARDING]:      'completed offboarding (annotator → Inactive)',
  [AUDIT_ACTIONS.CANCEL_OFFBOARDING]:        'cancelled offboarding',
  // Consent / activation
  [AUDIT_ACTIONS.ACTIVATE_ACCOUNT]:          'activated an account via consent token',
  // Tags
  [AUDIT_ACTIONS.UPDATE_DATASET_TAGS]:       'updated deliverable tags',
});

/**
 * Preferred audit-logging entry point.
 *
 * @param {string} action - Use an AUDIT_ACTIONS constant. Free-text allowed
 *                          for probe/experimental actions but discouraged.
 * @param {object} [meta]
 * @param {string} [meta.entityType] - Table the action touched (e.g., 'assignment', 'client_report').
 * @param {string} [meta.entityId]   - Row UUID the action touched.
 * @param {object} [meta.details]    - Arbitrary JSONB: before/after, reason, etc.
 *                                      Keep small (<10KB). Never include secrets.
 * @returns {Promise<string|null>} Event UUID on success, null on failure (never throws).
 */
export async function logAudit(action, meta = {}) {
  try {
    const { data, error } = await supabase.rpc('log_audit_event', {
      p_action: action,
      p_resource_type: meta.entityType || null,
      p_resource_id: meta.entityId || null,
      p_details: meta.details || null,
      p_ip: null,         // Browser can't reliably read client IP; server would capture at edge
      p_user_agent: typeof navigator !== 'undefined' ? navigator.userAgent?.slice(0, 500) : null,
    });
    if (error) throw error;
    return data;
  } catch (err) {
    // Fire-and-forget: never block user action on audit failure, but
    // report to Sentry so we catch systemic audit outages.
    captureError?.(err, { location: 'auditService.logAudit', action, entityType: meta.entityType });
    if (typeof console !== 'undefined') console.warn('[audit] logAudit failed:', err?.message || err);
    return null;
  }
}

/**
 * Backward-compatible API. Existing callers (JobPostingForm, useAdminActions,
 * AnnotatorProfile) use this shape. Internally uses the same RPC as logAudit
 * so events from old and new callers are indistinguishable in the table.
 *
 * The orgId/userId/userEmail parameters are ignored — the RPC reads them
 * from the JWT to prevent spoofing. We accept them to avoid breaking the
 * existing call sites.
 */
export async function logAdminAction({ orgId: _orgId, userId: _userId, userEmail: _userEmail, action, resourceType, resourceId, details }) {
  return logAudit(action, { entityType: resourceType, entityId: resourceId, details });
}

/**
 * Fetch audit-log entries for a specific entity (e.g. a single dataset's
 * history tab). RLS gates visibility to the caller's org.
 *
 * @param {string} resourceType — 'dataset' | 'job' | etc. (maps to admin_audit_log.resource_type)
 * @param {string} resourceId — UUID of the entity
 * @param {Object} [options]
 * @param {number} [options.limit=50] — max rows to return, newest first
 * @returns {Promise<Array>} rows with id / created_at / action / user_email / actor_role / details
 */
export async function fetchAuditLogForEntity(resourceType, resourceId, options = {}) {
  if (!resourceType || !resourceId) return [];
  const limit = typeof options.limit === 'number' && options.limit > 0 ? options.limit : 50;

  const { data, error } = await supabase
    .from('admin_audit_log')
    .select('id, created_at, action, user_email, actor_role, user_id, resource_type, resource_id, details')
    .eq('resource_type', resourceType)
    .eq('resource_id', resourceId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    captureError?.(error, { location: 'auditService.fetchAuditLogForEntity', resourceType, resourceId });
    return [];
  }
  return data || [];
}
